import React, { useMemo, useState } from "react";
import { getTopHelpfulActionsByEmotion } from "./StudentDataWellnessPracticeHelpfulnessDisplay2";
import EmotionPopup from "./EmotionPopup";
import { handleEmotion } from "../../StudentData/handlers/emotionHandler2";
import { BicepsFlexed, Info } from "lucide-react";

const EMOJI_MAP = [
  ["sad", "images/emotions/sad.png"],
  ["tired", "images/emotions/tired.png"],
  ["lonely", "images/emotions/lonely.png"],
  ["happy", "images/emotions/happy.png"],
  ["grateful", "images/emotions/grateful.png"],
  ["calm", "images/emotions/calm.png"],
  ["stressed", "images/emotions/stressed.png"],
  ["scared", "images/emotions/scared.png"],
  ["angry", "images/emotions/angry.png"],
  ["all emotions", "images/emotions/all_emotions.png"],
];

const getEmojiImage = (emotion) => {
  const match = EMOJI_MAP.find(([key]) => key === emotion);
  return match ? match[1] : null;
};

const getEmotionColor = (emotion) => {
  if (["sad", "tired", "lonely"].includes(emotion)) return "bg-[#F8FBFD]";
  if (["scared", "angry", "stressed"].includes(emotion)) return "bg-[#FCFAFE]";
  if (["grateful", "calm", "happy"].includes(emotion)) return "bg-[#F8FDFA]";
  return "bg-white";
};

const StudentDataWellnessSuccessTab2 = ({ state, expanded, setExpanded, name }) => {
  // const [expanded, setExpanded] = useState(false);
  const { perEmotion: topActions, globalActions } = useMemo(
    () => getTopHelpfulActionsByEmotion(state.filteredDataPoints),
    [state.filteredDataPoints],
  );
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [pendingEmotion, setPendingEmotion] = useState(null);
  const [filteredStudentEmotionData, setFilteredStudentEmotionData] =
    useState(null);
  const emotionCounts = state.counts || [];
  const totalEmotions = useMemo(
    () => emotionCounts.reduce((sum, e) => sum + e.current, 0),
    [emotionCounts],
  );
  const mostHelpfulGlobalAction = useMemo(
    () =>
      Object.entries(globalActions).sort(([, a], [, b]) => b - a)[0]?.[0] ||
      "- -",
    [globalActions],
  );

  const allEmotionsData = useMemo(
    () => ({ action: mostHelpfulGlobalAction }),
    [mostHelpfulGlobalAction],
  );
  const allEmotionNames = useMemo(
    () => (state.counts || []).map((e) => e.name),
    [state.counts],
  );

  const sortedEmotions = useMemo(() => {
    const emotionCountMap = emotionCounts.reduce((acc, emotion) => {
      acc[emotion.name] = emotion.current || 0;
      return acc;
    }, {});

    return allEmotionNames
      .map((emotion) => [emotion, topActions[emotion] || { action: "- -" }])
      .sort(
        (a, b) => (emotionCountMap[b[0]] || 0) - (emotionCountMap[a[0]] || 0),
      );
  }, [allEmotionNames, emotionCounts, topActions]);

  const displayEmotions = useMemo(
    () =>
      expanded
        ? [...sortedEmotions, ["all emotions", allEmotionsData]]
        : sortedEmotions.slice(0, 3),
    [allEmotionsData, expanded, sortedEmotions],
  );

  const handleToggle = () => setExpanded(!expanded);

  const handleEmotionsClick = (emotion) => {
    const calculated_emotions_data = handleEmotion(state, emotion);
    const filteredStudentEmotionData =
      calculated_emotions_data.studentEmotionData;
    setFilteredStudentEmotionData(filteredStudentEmotionData);

    if (!expanded) {
      setPendingEmotion(emotion);
      setExpanded(true);
    } else {
      setSelectedEmotion(emotion);
    }
  };

  return (
    <div className="flex-1 bg-white shadow rounded p-3 sm:p-4 2xl:p-8">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <BicepsFlexed className="w-5 h-5 text-600 shrink-0" />
          <h3 className="text-[22px] sm:text-[24px] font-bold">{name && state.chosenStudent && state.chosenStudent !== "All Students" ? 'What Helps ' + name : "What Helps Them"}</h3>
          <div className="group relative inline-block">
            <Info className="w-4 h-4 text-600 text-[#5b9cd6] hover:text-gray-600 cursor-pointer transition-colors" />
            <div className="absolute hidden group-hover:block right-1/2 sm:right-0 2xl:left-0 mt-2 w-48 sm:w-64 2xl:w-64 p-3 text-sm bg-gray-100 rounded shadow z-50">
              <span className="text-400 text-[14px]">
                Click any emotion to see the top 3 most helpful activities for
                all students and the most effective for each individual.
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={handleToggle}
          className="hidden sm:flex text-[12px] text-gray-600 items-center space-x-1"
        >
          <span>{expanded ? "View Less" : "View More"}</span>
          <span>
            {expanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            )}
          </span>
        </button>
      </div>
      <h4 className="text-400 text-[18px] mb-3 ml-7">
        Discover which wellness practices are most helpful.
      </h4>
      {selectedEmotion ? (
        <EmotionPopup
          emotion={selectedEmotion}
          counts={emotionCounts}
          totalEmotions={totalEmotions}
          topActions={topActions}
          onClose={() => setSelectedEmotion(null)}
          studentEmotionData={filteredStudentEmotionData}
          studentEmailToNameMap={state.studentEmailToNameMap}
          globalActions={globalActions}
        />
      ) : (
        <div className="bg-white">
          <div className="grid grid-cols-[1fr,1fr,auto] px-3 py-2 bg-gray-50 text-[#000000]/50 text-[10px] sm:text-[12px] uppercase font-bold border-b border-gray-200">
            <div>Emotion</div>
            <div>What's Helping</div>
            <div></div>
          </div>
          <div className="divide-y divide-gray-100">
            {displayEmotions.map(([emotion, data], index) => {
              const emotionData = emotionCounts.find((e) => e.name === emotion);
              const count = emotionData?.current || 0;
              const percentage =
                totalEmotions > 0
                  ? Math.round((count / totalEmotions) * 100)
                  : 0;

              return (
                <div
                  key={emotion}
                  onClick={() => handleEmotionsClick(emotion)}
                  className={`grid grid-cols-[1fr,1fr,auto] items-center px-3 py-3 group transition-colors duration-200 ease-in-out ${
                    index % 2 === 0 ? getEmotionColor(emotion) : "bg-white"
                  } hover:bg-blue-100 cursor-pointer`}
                >
                  {/* Emotion col */}
                  <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                    <span className="text-[16px] hidden sm:block text-gray-400">
                      {index + 1}
                    </span>
                    <img
                      src={getEmojiImage(emotion)}
                      alt={emotion}
                      className="h-5 w-5 shrink-0"
                    />
                    <span className="flex-1 truncate">
                      <span className="font-semibold capitalize text-600 text-[16px] truncate">
                        {emotion}
                      </span>
                      {emotion !== "all emotions" && (
                        <span className="ml-2 text-[16px] text-400">
                          ({percentage}%)
                        </span>
                      )}
                    </span>
                  </div>
                  {/* What's helping col */}
                  <div className="text-[16px] text-400 mx-2 sm:mx-0 min-w-0 whitespace-normal break-words">
                    {emotion === "all emotions"
                      ? data.action
                      : data.topAction || "- -"}
                  </div>
                  {/* Chevron */}
                  <div className="pr-1 sm:pr-4 justify-self-end">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-transform duration-200"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 9l6 6 6-6"
                      />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Mobile sticky View More/Less at bottom */}
      <div className="sm:hidden sticky bottom-0 bg-white border-t border-gray-200 p-2">
        <button
          onClick={handleToggle}
          className="w-full text-[12px] text-gray-600 flex items-center justify-center space-x-1"
        >
          <span>{expanded ? "View Less" : "View More"}</span>
          <span>
            {expanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            )}
          </span>
        </button>
      </div>
    </div>
  );
};

export default StudentDataWellnessSuccessTab2;
